import React, { useEffect, useRef } from 'react';
import { FaCheck, FaPen } from 'react-icons/fa';
import { REJIM_KISA } from './adliTatil';
import { birimAdi } from './ayarlar';

/** Rejime göre küçük renk noktası (adli tatil göstergesi). */
const REJIM_NOKTA = {
  kapali: 'bg-slate-300',
  hmk: 'bg-emerald-500',
  cmk: 'bg-indigo-500',
};

/**
 * Hazır süre paleti (ana ekran) — yatay kaydırılır çip satırı. Yalnızca favoriler gösterilir.
 * Kaydırma: mobil dokunma (native) + masaüstü fare tekerleği (dikey→yatay) + fareyle tıkla-sürükle.
 * presetler: gösterilecek (favori) preset listesi. selectedId | null. onSec(preset).
 */
function PresetSecici({ presetler, selectedId, onSec }) {
  const ref = useRef(null);
  const seciliRef = useRef(null);
  const drag = useRef({ down: false, startX: 0, startLeft: 0, moved: false });

  // Seçim değişince seçili çipi görünür alana getir (palet uzun/kaydırılmışsa kaybolmasın).
  useEffect(() => {
    const el = seciliRef.current;
    if (el) el.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
  }, [selectedId]);

  // Masaüstü: dikey tekerleği yatay kaydırmaya çevir (passive olmayan dinleyici → preventDefault çalışır).
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const handler = (e) => {
      if (el.scrollWidth <= el.clientWidth) return;
      if (e.deltaY === 0) return;
      el.scrollLeft += e.deltaY;
      e.preventDefault();
    };
    el.addEventListener('wheel', handler, { passive: false });
    return () => el.removeEventListener('wheel', handler);
  }, [presetler]);

  if (!presetler.length) return null;

  // Fareyle tıkla-sürükle (dokunma native kaydırmayı kullanır, dokunulmaz).
  const onPointerDown = (e) => {
    if (e.pointerType !== 'mouse' || e.button !== 0) return;
    drag.current = { down: true, startX: e.clientX, startLeft: ref.current.scrollLeft, moved: false };
  };
  const onPointerMove = (e) => {
    const d = drag.current;
    if (!d.down) return;
    const dx = e.clientX - d.startX;
    if (Math.abs(dx) > 3) d.moved = true;
    ref.current.scrollLeft = d.startLeft - dx;
  };
  const endDrag = () => { drag.current.down = false; };
  // Sürükleme olduysa tıklamayı yut (yanlışlıkla preset seçilmesin).
  const onClickCapture = (e) => {
    if (drag.current.moved) {
      e.preventDefault();
      e.stopPropagation();
      drag.current.moved = false;
    }
  };

  return (
    <div
      ref={ref}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerLeave={endDrag}
      onClickCapture={onClickCapture}
      className="scrollbar-hidden -mx-1 flex cursor-grab select-none gap-2 overflow-x-auto px-1 pb-1 active:cursor-grabbing"
    >
      {presetler.map((p) => {
        const secili = p.id === selectedId;
        return (
          <button
            key={p.id}
            ref={secili ? seciliRef : null}
            type="button"
            onClick={() => onSec(p)}
            aria-pressed={secili}
            title={p.manuel
              ? 'Süreyi elle gir'
              : `${p.sure} ${birimAdi(p.birim)} · ${REJIM_KISA[p.adliTatil]}${p.dayanak ? ` · ${p.dayanak}` : ''}${p.not ? `\n${p.not}` : ''}`}
            className={`inline-flex flex-none items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-all duration-200 ${
              secili
                ? 'border-amber-400/40 bg-gradient-to-b from-slate-700 to-slate-900 text-amber-300 shadow-md ring-1 ring-amber-400/30'
                : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50'
            }`}
          >
            {secili ? (
              <FaCheck className="h-2.5 w-2.5 flex-none text-amber-300" />
            ) : p.manuel ? (
              <FaPen className="h-2.5 w-2.5 flex-none text-slate-400" />
            ) : (
              <span className={`h-1.5 w-1.5 flex-none rounded-full ${REJIM_NOKTA[p.adliTatil] || 'bg-slate-300'}`} />
            )}
            {p.ad}
            {p.rozet ? (
              <span className={`ml-0.5 flex-none rounded-full px-1.5 py-px text-[9px] font-semibold uppercase tracking-wide ring-1 ring-inset ${
                secili ? 'bg-amber-400/15 text-amber-200 ring-amber-300/40' : 'bg-amber-50 text-amber-700 ring-amber-200'
              }`}>
                {p.rozet}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

export default PresetSecici;
